import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import toast from 'react-hot-toast';
import ContractFields from '../components/ContractFields';

const GREEN = '#0d1f3b';
const yy = String(new Date().getFullYear()).slice(-2);

// Common documents every service packet includes (HIA, Lien, Cancel) + Change Order (optional).
// Each service maps to its addendum. Roofing addendum will be added once provided.
const SERVICES = {
  coating: { label: 'Exterior Coating', addendum: 'coating', addendumName: 'Exterior Coating Addendum' },
  roofing: { label: 'Roofing', addendum: 'roofing', addendumName: 'Roofing Addendum' },
  windows: { label: 'Windows', addendum: 'windows', addendumName: 'Windows Addendum' },
};

// Build the default document checklist for a service.
function defaultDocs(service) {
  const svc = SERVICES[service];
  return [
    { type: 'hia', name: 'Home Improvement Agreement', on: true, required: true },
    svc ? { type: svc.addendum, name: svc.addendumName, on: true, required: true } : null,
    // Colours are only chosen on a coating job, so only offer it there.
    service === 'coating'
      ? { type: 'colors', name: 'Notice of Chosen Colors', on: true, required: false }
      : null,
    { type: 'change-order', name: 'Change Order Addendum', on: false, required: false },
    { type: 'lien', name: 'Mechanics Lien', on: true, required: false },
    { type: 'cancel', name: 'Three-Day Right to Cancel', on: true, required: false },
  ].filter(Boolean);
}

export default function SendContract() {
  const [service, setService] = useState('coating');
  const [docs, setDocs] = useState(defaultDocs('coating'));
  const [mode, setMode] = useState('new');           // 'new' | 'existing'
  const [fileNo, setFileNo] = useState('');           // auto for new, typed/picked for existing
  const [nextFile, setNextFile] = useState('');
  const [custQuery, setCustQuery] = useState('');
  const [matches, setMatches] = useState([]);
  const [f, setF] = useState({ ack: {}, coating: {}, trim: {}, other: {}, roofing: {}, windows: [{}, {}, {}] });
  const [link, setLink] = useState('');
  const [poPreview, setPoPreview] = useState('');
  const [saving, setSaving] = useState(false);
  const [sendPhone, setSendPhone] = useState('');
  const [sendEmail, setSendEmail] = useState('');
  const set = (k, v) => setF(s => ({ ...s, [k]: v }));

  // fetch the next auto file number for a new customer
  useEffect(() => {
    api.get('/contracts/next-file').then(r => setNextFile(String(r.data.file_no))).catch(() => {});
  }, []);

  useEffect(() => { setDocs(defaultDocs(service)); setLink(''); }, [service]);

  const effectiveFile = mode === 'new' ? nextFile : fileNo;
  const selectedTypes = docs.filter(d => d.on).map(d => d.type);

  function toggleDoc(type) {
    setDocs(ds => ds.map(d => d.type === type && !d.required ? { ...d, on: !d.on } : d));
  }

  async function searchCustomers() {
    if (!custQuery.trim()) return;
    try { const r = await api.get('/contracts/customers', { params: { q: custQuery.trim() } }); setMatches(r.data.results || []); }
    catch { setMatches([]); }
  }
  function pickCustomer(m) {
    setFileNo(m.file_no); setMode('existing');
    setF(s => ({ ...s, customer_name: m.customer_name || '', address: m.address || '', phone: m.phone || '', email: m.email || '' }));
    setMatches([]);
    toast.success(`Using file #${m.file_no} (${m.orders} existing order${m.orders > 1 ? 's' : ''})`);
  }

  async function createPacket() {
    if (!selectedTypes.length) { toast.error('Select at least one document.'); return; }
    if (mode === 'existing' && !fileNo.trim()) { toast.error('Enter or pick the existing file #.'); return; }
    setSaving(true);
    try {
      const body = { types: selectedTypes, prefill: f };
      if (mode === 'existing') body.file_no = fileNo.trim();
      const res = await api.post('/contracts/packet', body);
      const full = `${window.location.origin}${res.data.path}`;
      setLink(full); setPoPreview(res.data.po_preview || '');
      setSendEmail(f.email || ''); setSendPhone(f.phone || '');
      if (mode === 'new') setNextFile(String(parseInt(res.data.file_no) + 1));
      try { await navigator.clipboard.writeText(full); toast.success('Packet link copied.'); } catch { toast.success('Packet link created.'); }
    } catch (e) { toast.error('Could not create link. ' + (e?.response?.data?.error || '')); }
    finally { setSaving(false); }
  }

  return (
    <>
      <div className="page-header"><h2>Send Document Packet for Signature</h2></div>
      <div className="page-body">
        <div style={{ maxWidth: 860, margin: '0 auto' }}>
          <div style={{ background: '#fff', borderRadius: 14, padding: 24, border: '1px solid #eee' }}>

            <div style={{ background: '#eef1f6', border: '1px solid #cfd8e6', borderRadius: 10, padding: '12px 14px', marginBottom: 18, fontSize: 13, color: GREEN }}>
              📌 Build the signing packet, then send the customer <b>one link</b> for all documents. They review every document and sign once. The order is created automatically on signing as PO <b>{yy}-{effectiveFile || '____'}-01</b>.
            </div>

            {/* Service */}
            <label style={{ fontWeight: 700, fontSize: 13, color: GREEN, display: 'block', marginBottom: 6 }}>Service</label>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 18 }}>
              {Object.entries(SERVICES).map(([key, s]) => (
                <button key={key} type="button" onClick={() => setService(key)}
                  style={{ border: '1px solid ' + (service === key ? GREEN : '#ddd'), background: service === key ? GREEN : '#fff', color: service === key ? '#fff' : '#333', borderRadius: 9, padding: '8px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                  {s.label}
                </button>
              ))}
            </div>

            {/* Documents checklist */}
            <label style={{ fontWeight: 700, fontSize: 13, color: GREEN, display: 'block', marginBottom: 6 }}>Documents in this packet</label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 18 }}>
              {docs.map(d => (
                <label key={d.type} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13.5, cursor: d.required ? 'default' : 'pointer', opacity: d.required ? 0.9 : 1 }}>
                  <input type="checkbox" checked={d.on} disabled={d.required} onChange={() => toggleDoc(d.type)} />
                  {d.name}
                  {d.required && <span style={{ fontSize: 11, color: '#999' }}>(always included)</span>}
                  {d.type === 'change-order' && <span style={{ fontSize: 11, color: '#999' }}>(only if a change order is needed)</span>}
                </label>
              ))}
            </div>

            {/* Customer: new vs existing */}
            <label style={{ fontWeight: 700, fontSize: 13, color: GREEN, display: 'block', marginBottom: 6 }}>Customer</label>
            <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
              <button type="button" onClick={() => { setMode('new'); setFileNo(''); }}
                style={{ flex: 1, border: '1px solid ' + (mode === 'new' ? GREEN : '#ddd'), background: mode === 'new' ? GREEN : '#fff', color: mode === 'new' ? '#fff' : '#333', borderRadius: 9, padding: '9px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                New customer (auto file #{nextFile || '…'})
              </button>
              <button type="button" onClick={() => setMode('existing')}
                style={{ flex: 1, border: '1px solid ' + (mode === 'existing' ? GREEN : '#ddd'), background: mode === 'existing' ? GREEN : '#fff', color: mode === 'existing' ? '#fff' : '#333', borderRadius: 9, padding: '9px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                Existing customer (repeat order)
              </button>
            </div>

            {mode === 'existing' && (
              <div style={{ background: '#f7f8f9', border: '1px solid #e4e6e9', borderRadius: 10, padding: 12, marginBottom: 16 }}>
                <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                  <input value={custQuery} onChange={e => setCustQuery(e.target.value)} onKeyDown={e => e.key === 'Enter' && searchCustomers()}
                    placeholder="Search existing customer by name or address" style={{ flex: 1, fontSize: 13, padding: '8px 10px', border: '1px solid #ccc', borderRadius: 7 }} />
                  <button type="button" onClick={searchCustomers} className="btn btn-secondary btn-sm">Search</button>
                </div>
                {matches.map(m => (
                  <div key={m.file_no} onClick={() => pickCustomer(m)} style={{ padding: '8px 10px', borderRadius: 7, cursor: 'pointer', fontSize: 13, background: '#fff', border: '1px solid #e4e6e9', marginBottom: 5 }}>
                    <b>#{m.file_no}</b> — {m.customer_name} · {m.address} <span style={{ color: '#888' }}>({m.orders} order{m.orders > 1 ? 's' : ''})</span>
                  </div>
                ))}
                <div style={{ fontSize: 12, color: '#666', marginTop: 6 }}>Or type the file # directly:
                  <input value={fileNo} onChange={e => setFileNo(e.target.value.replace(/[^0-9A-Za-z]/g, ''))} placeholder="e.g. 1600"
                    style={{ marginLeft: 8, fontSize: 13, padding: '6px 9px', border: '1px solid #ccc', borderRadius: 7, width: 110 }} />
                  {fileNo && <span style={{ marginLeft: 10, fontWeight: 700, color: GREEN }}>→ next order: {yy}-{fileNo}-(auto)</span>}
                </div>
              </div>
            )}

            {/* The merged form: customer info + agreement + coating fields */}
            <div style={{ borderTop: '1px solid #eee', paddingTop: 16 }}>
              <div style={{ fontSize: 12.5, color: '#888', marginBottom: 10 }}>Fill the details below. Shared fields (name, address, price…) flow into every document in the packet.</div>
              <ContractFields type="hia" f={f} set={set} readOnly={false} />
              {selectedTypes.includes('coating') && (
                <div style={{ marginTop: 18, borderTop: '1px dashed #ddd', paddingTop: 14 }}>
                  <div style={{ fontWeight: 700, fontSize: 14, color: GREEN, marginBottom: 10 }}>Exterior Coating details</div>
                  <ContractFields type="coating" f={f} set={set} readOnly={false} />
                </div>
              )}
            </div>

            <button type="button" onClick={createPacket} disabled={saving} className="btn btn-primary" style={{ marginTop: 16 }}>
              {saving ? 'Creating…' : `🔗 Create signing link (${selectedTypes.length} doc${selectedTypes.length !== 1 ? 's' : ''})`}
            </button>

            {link && (
              <div style={{ marginTop: 16, background: '#f0f3f7', borderRadius: 10, padding: 14 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: GREEN, marginBottom: 6 }}>Packet link — order will be {poPreview}</div>
                <input readOnly value={link} onFocus={e => e.target.select()}
                  style={{ width: '100%', fontSize: 12, padding: '8px 10px', border: '1px solid #cfd8e6', borderRadius: 7, boxSizing: 'border-box' }} />
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10, alignItems: 'flex-end' }}>
                  <div><label style={{ fontSize: 11, color: '#555', display: 'block' }}>Customer mobile (WhatsApp)</label>
                    <input value={sendPhone} onChange={e => setSendPhone(e.target.value)} placeholder="+1 626 555 0123" style={{ fontSize: 13, padding: '7px 9px', border: '1px solid #ccc', borderRadius: 7, width: 180 }} /></div>
                  <div><label style={{ fontSize: 11, color: '#555', display: 'block' }}>Customer email</label>
                    <input value={sendEmail} onChange={e => setSendEmail(e.target.value)} placeholder="name@email.com" style={{ fontSize: 13, padding: '7px 9px', border: '1px solid #ccc', borderRadius: 7, width: 180 }} /></div>
                </div>
                <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
                  <a href={`https://wa.me/${sendPhone.replace(/[^0-9]/g, '')}?text=${encodeURIComponent('Please review and sign your documents here: ' + link)}`} target="_blank" rel="noopener noreferrer"
                    style={{ background: '#25D366', color: '#fff', fontWeight: 700, fontSize: 13, padding: '9px 16px', borderRadius: 8, textDecoration: 'none', pointerEvents: sendPhone.replace(/[^0-9]/g, '') ? 'auto' : 'none', opacity: sendPhone.replace(/[^0-9]/g, '') ? 1 : 0.5 }}>💬 WhatsApp</a>
                  <a href={`mailto:${sendEmail}?subject=${encodeURIComponent('Your documents to sign — Amanda\'s Home Improvement')}&body=${encodeURIComponent('Hello,\n\nPlease review and sign your documents here:\n' + link + '\n\nThank you,\nAmanda\'s Home Improvement')}`}
                    style={{ background: GREEN, color: '#fff', fontWeight: 700, fontSize: 13, padding: '9px 16px', borderRadius: 8, textDecoration: 'none', pointerEvents: sendEmail ? 'auto' : 'none', opacity: sendEmail ? 1 : 0.5 }}>✉️ Email</a>
                  <button type="button" onClick={async () => { try { await navigator.clipboard.writeText(link); toast.success('Copied'); } catch {} }}
                    style={{ background: '#fff', color: GREEN, border: '1px solid ' + GREEN, fontWeight: 700, fontSize: 13, padding: '9px 16px', borderRadius: 8, cursor: 'pointer' }}>📋 Copy</button>
                </div>
                <div style={{ fontSize: 11, color: '#777', marginTop: 8 }}>Expires in 14 days or once signed. The order appears in the CRM after signing.</div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
